import axios from "axios";
import { useRef, useContext, useState, useCallback, FormEvent } from "react";
import { AiOutlineSend, AiOutlineIdcard } from "react-icons/ai";
import { BiUserCircle, BiCalendar } from "react-icons/bi";
import { FiDollarSign, FiMap, FiMapPin, FiPhone } from "react-icons/fi";

import { Divider } from "../../components/Divider";
import { Input } from "../../components/Input";
import { TextArea } from "../../components/TextArea";
import {
  API_URL,
  IS_APP_DRIVER,
  INVESTMENT_INTEREST,
  INVESTMENT_CAPITAL_OPTIONS,
} from "../../constants";
import { UIContext } from "../../UIContext";
import styles from "./styles.module.css";
import { SelectWithLiveSearch } from "../../components/SelectWithLiveSearch";

const axiosInstance = axios.create({
  baseURL: API_URL,
});

export const Send = (): JSX.Element => {
  const formRef = useRef<HTMLFormElement>(null);
  const { success } = useContext(UIContext);
  const [error, setError] = useState<string | null>(null);
  const [isDriver, setIsDriver] = useState<string[]>([]);

  const [investmentCapital, setInvestmentCapital] = useState<string[]>([]);
  const [capitalPretention, setCapitalPretention] = useState<string[]>([]);

  const hasIsDriver = useCallback(
    (driver: string) => isDriver.includes(driver),
    [isDriver]
  );

  const handleIsDriver = useCallback(
    (driver: string) => {
      if (isDriver.includes(driver)) {
        const filteredDrivers = isDriver.filter(
          (currentDriver) => currentDriver !== driver
        );

        setIsDriver(filteredDrivers);
        return;
      }

      setIsDriver((currentDrivers) => currentDrivers.concat(driver));
    },
    [isDriver]
  );

  const hasCapitalInvestment = useCallback(
    (investment: string) => investmentCapital.includes(investment),
    [investmentCapital]
  );

  const handleCapitalInvestment = useCallback(
    (investment: string) => {
      if (investmentCapital.includes(investment)) {
        const filteredInvestments = investmentCapital.filter(
          (currentInvestment) => currentInvestment !== investment
        );

        setInvestmentCapital(filteredInvestments);
        return;
      }

      setInvestmentCapital((currentInvestments) =>
        currentInvestments.concat(investment)
      );
    },
    [investmentCapital]
  );

  const hasCapitalUsagePretention = useCallback(
    (pretention: string) => capitalPretention.includes(pretention),
    [capitalPretention]
  );

  const handleCapitalUsagePretention = useCallback(
    (pretention: string) => {
      if (capitalPretention.includes(pretention)) {
        const filteredPretentions = capitalPretention.filter(
          (currentPretention) => currentPretention !== pretention
        );

        setCapitalPretention(filteredPretentions);
        return;
      }

      setCapitalPretention((currentPretentions) =>
        currentPretentions.concat(pretention)
      );
    },
    [capitalPretention]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();

      setError(null);

      const elements = Array.from(
        formRef.current?.querySelectorAll(
          "input, .input-container-liscense, .input-container-profile, .input-container [name], textarea"
        ) as any
      ) as any[];
      const data = elements.reduce(
        (obj, element) => ({
          ...obj,
          [element.name]: element.value,
        }),
        {}
      );

      try {
        await axiosInstance.post("register", {
          ...data,
          is_app_driver: isDriver,
          investment_interest: capitalPretention,
          investment_capital_options: investmentCapital,
          birthdate: String(data.birthdate).split("/").reverse().join("-"),
        });

        success();
      } catch (newError: any) {
        const message =
          newError.response?.data.message ||
          "Ocorreu um erro inesperado, tente novamente!";

        setError(message);
      }
    },
    [isDriver, investmentCapital, capitalPretention, success]
  );

  return (
    <form ref={formRef} onSubmit={handleSubmit} className={styles.container}>
      <h2 className="error">{error}</h2>

      <div className="section-title">
        <h3>Dados Pessoais</h3>
      </div>

      <div className="input-container">
        <Input
          name="full_name"
          icon={BiUserCircle}
          placeholder="Nome Completo"
        />
        <Input
          mask="99/99/9999"
          name="birthdate"
          icon={BiCalendar}
          placeholder="Data de Nascimento"
        />
        <Input
          name="cpf"
          mask="999.999.999-99"
          icon={AiOutlineIdcard}
          placeholder="CPF"
        />
        <Input
          name="phone"
          mask="+55 (99) 9 9999-9999"
          icon={FiPhone}
          placeholder="Telefone"
        />
      </div>

      <Divider opacity={0.5} background="#979797" />

      <div className="section-title">
        <h3>Endereço Atual</h3>
      </div>

      <div className="input-container">
        <Input name="city" icon={FiMapPin} placeholder="Cidade Atual" />
        <Input name="state" icon={FiMap} placeholder="Estado" />
      </div>

      <Divider opacity={0.5} background="#979797" />

      <div className="section-title">
        <h3>Perfil do Candidato</h3>
      </div>

      <div className="input-container-profile">
        <div className="checkbox-container">
          <h3>Você é motorista por aplicativo?</h3>
          {IS_APP_DRIVER.map((driver) => (
            <div key={driver}>
              <input
                type="checkbox"
                onChange={() => handleIsDriver(driver)}
                checked={hasIsDriver(driver)}
              />
              <span>{driver}</span>
            </div>
          ))}
        </div>

        <Input
          name="gross_income"
          label="Qual sua Renda Bruta?"
          icon={FiDollarSign}
          placeholder="Ex: R$ 4000,00"
        />

        <div className="checkbox-container">
          <h3>
            Você pretende utilizar parte dessa renda para investir na Urbano
            Norte?
          </h3>
          {INVESTMENT_INTEREST.map((pretention) => (
            <div key={pretention}>
              <input
                type="checkbox"
                onChange={() => handleCapitalUsagePretention(pretention)}
                checked={hasCapitalUsagePretention(pretention)}
              />
              <span>{pretention}</span>
            </div>
          ))}
        </div>
        <TextArea
          name="has_business_experience"
          label="Você já atua como empresário? nos conte sua experiência *"
          placeholder="Digite aqui sua resposta..."
          rows={3}
        />
      </div>

      <Divider opacity={0.5} background="#979797" />

      <div className="section-title">
        <h3>Interesse no Licenciamento Urbano Norte</h3>
      </div>

      <div className="input-container-liscense">
        <SelectWithLiveSearch
          name="target_city_or_region"
          label="Qual cidade ou região você quer levar a Urbano Norte?"
          placeholder="Procure por uma cidade..."
          onCitySelect={(city) => {
            console.log("Cidade selecionada:", city);
          }}
        />

        <TextArea
          name="how_did_you_hear_about_us"
          label="Como conheceu a Urbano Norte? O que mais chamou sua atenção no projeto?"
          placeholder="Digite aqui sua resposta..."
          rows={3}
        />

        <TextArea
          name="city_growth_potential"
          label="Porque você acredita no potencial dessa cidade?"
          placeholder="Digite aqui sua resposta..."
          rows={3}
        />

        <div className="checkbox-container">
          <h4>Qual o seu Capital de Investimento?</h4>
          {INVESTMENT_CAPITAL_OPTIONS.map((investment) => (
            <div key={investment}>
              <input
                type="checkbox"
                onChange={() => handleCapitalInvestment(investment)}
                checked={hasCapitalInvestment(investment)}
              />
              <span>{investment}</span>
            </div>
          ))}
        </div>
      </div>

      <button type="submit">
        <span>Enviar</span>
        <AiOutlineSend />
      </button>
    </form>
  );
};
